import type { FC } from 'react';
import { Button, Card, Stack, Typography } from '@mui/material';
import type { LabelColor } from '@pflegenavi/web-components';
import { Iconify, Label } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import type {
  UBO,
  UBODeclaration,
} from '@pflegenavi/service-provider-frontend/api';
import { useFormatDate } from '@pflegenavi/frontend/localization';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';

interface UboCardProps {
  ubo: UBO;
  onDelete?: (uboId: string) => void;
}

const UboCard: FC<UboCardProps> = ({ ubo, onDelete }) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();

  return (
    <Card data-testid="ubo-card" sx={{ p: 2 }}>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography data-testid="ubo-name" variant="subtitle1">
            {ubo.FirstName} {ubo.LastName}
          </Typography>
          {onDelete && (
            <Button
              data-testid="ubo-delete-button"
              color="error"
              onClick={() => onDelete(ubo.Id)}
              startIcon={<Iconify icon="eva:trash-2-outline" />}
            >
              {t('actions.delete')}
            </Button>
          )}
        </Stack>
        <Typography variant="body2">
          {t('kyc.ubo.born-in', {
            date: fDate(
              new Date(ubo.Birthday * 1000),
              YEAR_MONTH_DAY_SHORT_FORMAT
            ),
            city: ubo.Birthplace.City,
            country: ubo.Birthplace.Country,
          })}
        </Typography>
        <Typography variant="body2">
          {ubo.Address.AddressLine1}, {ubo.Address.PostalCode}{' '}
          {ubo.Address.City}, {ubo.Address.Country}
        </Typography>
      </Stack>
    </Card>
  );
};

interface UboDeclarationViewProps {
  declaration?: UBODeclaration;
  onCreateDeclaration: () => void;
  onAddUbo: () => void;
  onSubmitDeclaration: () => void;
  onDeleteUbo?: (uboId: string) => void;
}

export const UboDeclarationView: FC<UboDeclarationViewProps> = ({
  declaration,
  onCreateDeclaration,
  onAddUbo,
  onSubmitDeclaration,
  onDeleteUbo,
}) => {
  const { t } = useTranslation();

  let statusColor: LabelColor = 'default';
  if (declaration?.Status === 'VALIDATED') {
    statusColor = 'success';
  } else if (
    declaration?.Status === 'VALIDATION_ASKED' ||
    declaration?.Status === 'INCOMPLETE'
  ) {
    statusColor = 'warning';
  } else if (declaration?.Status === 'REFUSED') {
    statusColor = 'error';
  }

  if (!declaration || declaration.Status === 'REFUSED') {
    return (
      <Stack
        data-testid="ubo-declaration-section"
        sx={{
          gap: 2,
        }}
      >
        <Typography variant="h6">{t('kyc.ubo-declaration')}</Typography>
        <Typography variant="body2">
          {t('kyc.ubo-declaration-description')}
        </Typography>
        {declaration && (
          <Label color={statusColor}>
            {t(`kyc.ubo.status.${declaration.Status.toLowerCase()}`)}
          </Label>
        )}
        <Button
          data-testid="create-ubo-declaration-button"
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={onCreateDeclaration}
        >
          {t('kyc.ubo.create-declaration')}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      data-testid="ubo-declaration-section"
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="h6">{t('kyc.ubo-declaration')}</Typography>
      <Typography variant="body2">
        {t('kyc.ubo-declaration-description')}
      </Typography>
      <Label color={statusColor} data-testid="ubo-declaration-status">
        {t(`kyc.ubo.status.${declaration.Status.toLowerCase()}`)}
      </Label>
      {(declaration.Status === 'CREATED' ||
        declaration.Status === 'INCOMPLETE') && (
        <>
          {declaration.Ubos.map((ubo) => (
            <UboCard key={ubo.Id} ubo={ubo} onDelete={onDeleteUbo} />
          ))}
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Button
              data-testid="add-ubo-button"
              variant="outlined"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={onAddUbo}
            >
              {t('kyc.ubo.add')}
            </Button>
            <Button
              data-testid="submit-ubo-declaration-button"
              variant="contained"
              disabled={declaration.Ubos.length === 0}
              onClick={onSubmitDeclaration}
            >
              {t('kyc.ubo.submit-declaration')}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
